import classNames from "classnames";
import { useContext } from "react";
import type { Components } from "react-markdown";
import { Anchor } from "../anchor";
import { List } from "../list";
import { Text } from "../text";
import { Tooltip } from "../tooltip";
import { MarkdownImage } from "./markdown-image";
import { CLASSNAME_REGEX, STRONG_CLASSNAME_REGEX } from "./markdown.constants";
import type { IMarkdownElementProps } from "./markdown.types";
import { MarkdownContext } from "./markdown.context";
import styles from "./markdown.module.scss";
import { getChildrenText } from "./markdown.utilities";

/**
 * Paragraph.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The paragraph as a `Text`.
 */
const MarkdownParagraph = ({ children, ref, ...otherProps }: IMarkdownElementProps<"p">) => {
    const { textProps } = useContext(MarkdownContext);
    if (typeof children === "undefined") return null;

    return (
        <Text {...textProps} {...otherProps}>
            {children}
        </Text>
    );
};

/**
 * Preformatted block.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The block as a wrapping `Text`.
 */
const MarkdownPre = ({ children, ref, ...otherProps }: IMarkdownElementProps<"pre">) => {
    const { textProps } = useContext(MarkdownContext);
    if (typeof children === "undefined") return null;

    return (
        <Text component="pre" style={{ whiteSpace: "pre-wrap" }} {...textProps} {...otherProps}>
            {children}
        </Text>
    );
};

/**
 * Inline code.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The code as a `Text`.
 */
const MarkdownCode = ({ children, ref, ...otherProps }: IMarkdownElementProps<"code">) => {
    const { textProps } = useContext(MarkdownContext);
    if (typeof children === "undefined") return null;

    return (
        <Text component="code" {...textProps} {...otherProps}>
            {children}
        </Text>
    );
};

/**
 * Bullet list.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The list as a `List`.
 */
const MarkdownUnorderedList = ({ children, ref, ...otherProps }: IMarkdownElementProps<"ul">) => {
    const { listProps } = useContext(MarkdownContext);
    if (typeof children === "undefined") return null;

    // Default to a disc marker. Mantine's `List` sets `list-style: none`, so without this
    // every caller has to remember `listProps={{ listStyleType: "disc" }}` just to get the
    // bullets markdown implies — and a caller that forgets renders an indented block with no
    // markers at all. Declared before `listProps` so callers can still override it.
    return (
        <List listStyleType="disc" {...listProps} {...otherProps}>
            {children}
        </List>
    );
};

/**
 * Numbered list.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The list as an ordered `List`.
 */
// `type` is pulled out of the props on purpose. `react-markdown` passes through the HTML `<ol type>` attribute,
// typed `"a" | "i" | "1" | "A" | "I" | "ordered"`, while Mantine's `List` has its own unrelated `type` of
// `"ordered" | "unordered"`. They collided — and since the spread came last it could also overwrite the
// `"ordered"` below with a value `List` does not understand. Removing it from the spread resolves the conflict
// and removes the `as any` that was papering over it.
const MarkdownOrderedList = ({ children, ref, type, ...otherProps }: IMarkdownElementProps<"ol">) => {
    const { listProps } = useContext(MarkdownContext);
    if (typeof children === "undefined") return null;

    // Force a decimal marker on ordered lists. Callers commonly pass listProps={listStyleType:
    // "disc"} to restore markers despite the global reset; without this override, ordered lists
    // would render with disc bullets and lose their numbering.
    return (
        <List {...listProps} listStyleType="decimal" type="ordered" {...otherProps}>
            {children}
        </List>
    );
};

/**
 * List item.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The item as a `List.Item`.
 */
const MarkdownListItem = ({ children, ref, ...otherProps }: IMarkdownElementProps<"li">) => {
    const { listItemProps } = useContext(MarkdownContext);
    if (typeof children === "undefined") return null;

    return (
        <List.Item {...listItemProps} {...otherProps}>
            {children}
        </List.Item>
    );
};

/**
 * Link. Non-image attachments arrive as `[filename](url)` and render through here — a plain anchor that requests
 * nothing, which is why they degrade acceptably already.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The link as an `Anchor`.
 */
const MarkdownAnchor = ({ node, ref, ...props }: IMarkdownElementProps<"a">) => {
    const { textProps, anchorProps, linkTarget = "_blank" } = useContext(MarkdownContext);
    const { children, ...otherProps } = props;
    if (typeof children === "undefined") return null;

    return (
        <Anchor
            {...textProps}
            {...otherProps}
            {...anchorProps}
            style={{ wordBreak: "break-word" }}
            target={linkTarget}
            rel={linkTarget === "_blank" ? "noreferrer noopener" : undefined}
        >
            {children}
            {linkTarget === "_blank" && (
                <Text {...textProps} span className={styles["screen-reader-text"]}>
                    {" "}
                    this link will open in a new tab
                </Text>
            )}
        </Anchor>
    );
};

/**
 * Bold text, which also carries the writing-issue and class-name marker syntax.
 * @param props The properties `react-markdown` supplies for the element.
 * @returns The text, wrapped in a tooltip when it carries a marker.
 */
const MarkdownStrong = ({ children }: IMarkdownElementProps<"strong">) => {
    const { textProps } = useContext(MarkdownContext);
    if (typeof children === "undefined") return null;

    const { className, ...otherTextProps } = textProps || {};
    // Allow a class name and description with the syntax:
    // "**issueStart[className,description]textissueEnd**".
    // The marker can span a newline, which `preserveNewlines` turns into a `<br>` element among
    // the children; `getChildrenText` rebuilds the plain text (mapping `<br>` back to "\n") so a
    // plain `children.join("")` cannot break the match by stringifying that element as
    // "[object Object]".
    const textContent = getChildrenText(children);
    const classNameMatch = textContent.match(CLASSNAME_REGEX);
    if (classNameMatch) {
        return (
            <Tooltip
                label={classNameMatch[2]}
                disabled={
                    classNameMatch.length < 3 ||
                    typeof classNameMatch[2] === "undefined" ||
                    classNameMatch[2].trim() === ""
                }
            >
                <Text span className={classNames(className, classNameMatch[1])} {...otherTextProps}>
                    {classNameMatch[3]}
                </Text>
            </Tooltip>
        );
    }

    const match = textContent.match(STRONG_CLASSNAME_REGEX);
    if (match) {
        return (
            <Tooltip
                label={match[2]}
                disabled={match.length < 3 || typeof match[2] === "undefined" || match[2].trim() === ""}
            >
                <Text
                    variant="semibold"
                    span
                    className={classNames(className, match[1])}
                    role="strong"
                    {...otherTextProps}
                >
                    {match[3]}
                </Text>
            </Tooltip>
        );
    }

    return (
        <Text variant="semibold" {...textProps} span>
            {children}
        </Text>
    );
};

/**
 * The element components `Markdown` hands to `react-markdown`.
 *
 * This must be a module-level constant, and every entry a module-level component. `react-markdown` uses these as
 * element *types*, so a map rebuilt on each render — or one entry recreated on each render — is a different type on
 * each render, and React unmounts and remounts the whole rendered markdown. For images that means the browser
 * re-requests every attachment whenever the surrounding message re-renders (hover, audio position, tags,
 * translation), and an image whose URL fails is caught in a loop: request, fail, re-render, remount, request again.
 * Anything these components need from the caller reaches them through `MarkdownContext`, whose value changing
 * re-renders them without replacing the elements they produced.
 */
export const MARKDOWN_COMPONENTS: Components = {
    a: MarkdownAnchor,
    code: MarkdownCode,
    img: MarkdownImage,
    li: MarkdownListItem,
    ol: MarkdownOrderedList,
    p: MarkdownParagraph,
    pre: MarkdownPre,
    strong: MarkdownStrong,
    ul: MarkdownUnorderedList,
};
